import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Label2, StatusBadge } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, ShieldCheck, Stethoscope, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const TONE = { erstellt: "gray", diagnostiziert: "blue", abgeschlossen: "green" };
const WAR_TONE = { in_garantie: "green", keine_garantie: "amber" };

export default function Repairs() {
  const [items, setItems] = useState(null);
  const [assets, setAssets] = useState([]);
  const [open, setOpen] = useState(false);
  const [diag, setDiag] = useState(null);
  const [form, setForm] = useState({ asset_id: "", title: "", description: "" });
  const [dform, setDform] = useState({ diagnosis: "", root_cause: "", replacement_recommended: false });

  const load = useCallback(() => { api.get("/repairs").then((r) => setItems(r.data)); api.get("/assets").then((r) => setAssets(r.data)); }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try { await api.post("/repairs", form); toast.success("Reparatur angelegt"); setOpen(false); setForm({ asset_id: "", title: "", description: "" }); load(); }
    catch (e) { toast.error("Fehler", { description: formatApiError(e.response?.data?.detail) }); }
  };
  const warranty = async (id) => { const { data } = await api.post(`/repairs/${id}/warranty-check`); toast.success(`Garantie: ${data.warranty_status}`); load(); };
  const diagnose = async () => { await api.post(`/repairs/${diag.id}/diagnose`, dform); toast.success("Diagnose gespeichert"); setDiag(null); setDform({ diagnosis: "", root_cause: "", replacement_recommended: false }); load(); };
  const complete = async (id) => { await api.post(`/repairs/${id}/complete`, {}); toast.success("Reparatur abgeschlossen"); load(); };

  if (!items) return <div className="space-y-2">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-md" />)}</div>;

  return (
    <div>
      <PageHeader title="Reparaturen" subtitle="Instandsetzung · Garantie · Diagnose (RCA) · Teiletausch">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-repair-button" className="gap-2"><Plus size={18} weight="bold" />Neue Reparatur</Button></DialogTrigger>
          <DialogContent data-testid="repair-dialog"><DialogHeader><DialogTitle className="font-heading">Reparatur anlegen</DialogTitle></DialogHeader>
            <DialogDescription>Instandsetzungsauftrag für eine Anlage.</DialogDescription>
            <div className="space-y-3">
              <div className="space-y-1.5"><Label2>Anlage *</Label2>
                <Select value={form.asset_id} onValueChange={(v) => setForm((f) => ({ ...f, asset_id: v }))}>
                  <SelectTrigger data-testid="repair-asset"><SelectValue placeholder="Anlage wählen" /></SelectTrigger>
                  <SelectContent>{assets.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label2>Titel *</Label2><Input data-testid="repair-title" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label2>Beschreibung</Label2><Textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} /></div>
            </div>
            <DialogFooter><Button data-testid="repair-save" onClick={submit} disabled={!form.asset_id || !form.title}>Anlegen</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <Card className="divide-y divide-border rounded-md border">
        {items.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">Keine Reparaturen.</div>}
        {items.map((r) => (
          <div key={r.id} data-testid="repair-row" className="flex flex-col justify-between gap-3 px-5 py-4 sm:flex-row sm:items-center">
            <div><div className="font-semibold">{r.title}</div>
              <div className="text-xs text-muted-foreground">{r.asset_name}{r.root_cause ? ` · Ursache: ${r.root_cause}` : ""}</div></div>
            <div className="flex flex-wrap items-center gap-2">
              {r.warranty_status && <StatusBadge tone={WAR_TONE[r.warranty_status]}>{r.warranty_status.replace("_", " ")}</StatusBadge>}
              <StatusBadge tone={TONE[r.status]}>{r.status}</StatusBadge>
              {!r.warranty_status && <Button size="sm" variant="outline" className="gap-1.5" onClick={() => warranty(r.id)}><ShieldCheck size={15} />Garantie</Button>}
              {r.status !== "abgeschlossen" && <Button size="sm" variant="outline" className="gap-1.5" data-testid={`diagnose-${r.id}`} onClick={() => setDiag(r)}><Stethoscope size={15} />Diagnose</Button>}
              {r.status === "diagnostiziert" && <Button size="sm" className="gap-1.5" onClick={() => complete(r.id)}><CheckCircle size={15} />Abschließen</Button>}
            </div>
          </div>
        ))}
      </Card>

      <Dialog open={!!diag} onOpenChange={(o) => !o && setDiag(null)}>
        <DialogContent data-testid="diagnose-dialog"><DialogHeader><DialogTitle className="font-heading">Diagnose & Ursachenanalyse</DialogTitle></DialogHeader>
          <DialogDescription>Root-Cause-Analyse und Ersatzteilempfehlung.</DialogDescription>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label2>Diagnose</Label2><Textarea data-testid="diag-diagnosis" value={dform.diagnosis} onChange={(e) => setDform((f) => ({ ...f, diagnosis: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label2>Grundursache (RCA)</Label2><Input data-testid="diag-rootcause" value={dform.root_cause} onChange={(e) => setDform((f) => ({ ...f, root_cause: e.target.value }))} /></div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={dform.replacement_recommended} onChange={(e) => setDform((f) => ({ ...f, replacement_recommended: e.target.checked }))} />Austausch empfohlen</label>
          </div>
          <DialogFooter><Button data-testid="diag-save" onClick={diagnose} disabled={!dform.diagnosis}>Speichern</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
